import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";

import { ModuleBody, InlineLoading } from "@wfp/ui";

import { checkCol } from "components/CountryAdmin/CountryAdminContent/Table/Utils";
import Table from "components/CountryAdmin/CountryAdminContent/Table/Table";
import Filtering from "components/CountryAdmin/CountryAdminContent/Table/Filtering";

import { getCurrentUser } from "data-handler/ducks/auth";

import "../CsvImportModal/_content.scss";

const CompactVendors = ({
  selectedVendorsIds,
  vendors = [],
  onLoadPageDispatch,
  getVendors,
}) => {
  const intl = useIntl();
  const allVendors = useSelector(getVendors);
  const currentUser = useSelector(getCurrentUser);

  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isFetching = useSelector((state) => state.vendors.fetching);
  const [selectedAll, setSelectedAll] = useState(false);
  const [filtersState, setFilters] = useState([]);

  const { count = 0, ids = [] } = allVendors;

  const data = vendors.concat(
    ids
      .filter((id) => !vendors.find((vendor) => vendor.id === id))
      .map((id) => allVendors.results[id])
  );

  const filterFields = [
    {
      name: "name",
      label: intl.formatMessage({
        id: "CountryAdmin.name",
        defaultMessage: "Name",
      }),
    },
    {
      name: "vendor_category",
      label: intl.formatMessage({
        id: "SchoolVendorsShow.VendorType",
        defaultMessage: "Vendor type",
      }),
    },
    {
      name: "ref_number",
      label: intl.formatMessage({
        id: "SchoolVendorsShow.ref-no",
        defaultMessage: "Reference no.",
      }),
    },
    {
      name: "adm_4_name",
      label: intl.formatMessage({
        id: "CountryAdmin.adminFour",
        defaultMessage: "Admin 4",
      }),
    },
    {
      name: "adm_3_name",
      label: intl.formatMessage({
        id: "CountryAdmin.adminThree",
        defaultMessage: "Admin 3",
      }),
    },
    {
      name: "adm_2_name",
      label: intl.formatMessage({
        id: "CountryAdmin.adminTwo",
        defaultMessage: "Admin 2",
      }),
    },
    {
      name: "adm_1_name",
      label: intl.formatMessage({
        id: "CountryAdmin.adminOne",
        defaultMessage: "Admin 1",
      }),
    },
  ];

  const filterProps = {
    fields: filterFields,
    onSubmit: (e) => {
      const filters = Object.keys(e).map((key) => ({ id: key, value: e[key] }));
      setFilters(filters);
      loadPage(pageSize, 1, "", filters);
    },
    defaults: filtersState.reduce((a, x) => ({ ...a, [x.id]: x.value }), {}),
    secondaryButton: {
      onSubmit: (e) => {
        const filters = Object.keys(e).map((key) => ({
          id: key,
          value: e[key],
        }));

        setFilters(filters);
        onLoadPageDispatch(99999, 1, filters, "");
      },
      name: intl.formatMessage({
        id: "CountryAdmin.selectAll",
        defaultMessage: "Select all",
      }),
      secondaryName: intl.formatMessage({
        id: "CountryAdmin.unselectAll",
        defaultMessage: "Unselect all",
      }),
      secondaryButtonState: {
        setSelectedAll: (e) => {
          setSelectedAll(e);
        },
        selectedAllState: selectedAll,
      },
      selectedVendorsIds: selectedVendorsIds,
    },
    showSecondaryButton: true,
  };

  const columns = useMemo(() => {
    const allColumns = [
      checkCol,
      {
        Header: intl.formatMessage({
          id: "SchoolVendorsShow.VendorName",
          defaultMessage: "Vendor name",
        }),
        accessor: "name",
      },
      {
        Header: intl.formatMessage({
          id: "SchoolVendorsShow.VendorType",
          defaultMessage: "Vendor type",
        }),
        accessor: "vendor_category.name",
      },
      {
        Header: intl.formatMessage({
          id: "SchoolVendorsShow.ref-no",
          defaultMessage: "Reference no.",
        }),
        accessor: "ref_number",
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.adminFour",
          defaultMessage: "Admin 4",
        }),
        accessor: "adm_4.name",
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.adminThree",
          defaultMessage: "Admin 3",
        }),
        accessor: "adm_3.name",
        disableSortBy: true,
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.adminTwo",
          defaultMessage: "Admin 2",
        }),
        accessor: "adm_2.name",
        disableSortBy: true,
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.adminOne",
          defaultMessage: "Admin 1",
        }),
        accessor: "adm_1.name",
        disableSortBy: true,
      },
    ];
    if (currentUser?.is_admin) {
      allColumns.push({
        Header: intl.formatMessage({
          id: "CountryAdmin.country",
          defaultMessage: "Country",
        }),
        accessor: "country.name",
      });
    }
    return allColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPage = (size, idx, ordering, filters = filtersState) => {
    setPageSize(size);
    setPageNum(idx);
    onLoadPageDispatch(size, idx, filters, ordering);
  };

  const setSelected = (ids) => {
    const selectedRowObjectList = Object.keys(ids)
      .map((idx) => data[parseInt(idx)])
      .reduce((a, x) => {
        return { ...a, [x.id]: x };
      }, {});

    selectedVendorsIds.current = {
      ...selectedVendorsIds.current,
      ...selectedRowObjectList,
    };

    const currentPageUnselectedVendors = data
      .filter((item, index) => {
        return !Object.keys(ids).includes(index.toString());
      })
      .map((item) => item.id);
    currentPageUnselectedVendors.forEach((item) => {
      if (item in selectedVendorsIds.current) {
        delete selectedVendorsIds.current[item];
      }
    });
  };

  const getCurrentPageSelectedRows = () => {
    let selectedRows = {};
    data.forEach((item, index) => {
      if (
        Object.keys(selectedVendorsIds.current).includes(item.id.toString())
      ) {
        selectedRows[index] = true;
      }
    });
    return selectedRows;
  };

  const tableProps = {
    columns,
    data,
    pageNum,
    pageSize,
    totalItems: count,
    loadPage,
    setSelected,
    initialState: {
      selectedRowIds:
        selectedVendorsIds.current && !selectedAll
          ? getCurrentPageSelectedRows()
          : selectedVendorsIds.current && selectedAll
          ? ids
          : {},
    },
  };

  return (
    <div className="country-admin-main-content vendors-table">
      <div className="compact-vendors-table">
        <ModuleBody>
          <div style={isFetching ? { display: "none" } : {}}>
            <Filtering {...filterProps} />
            <Table {...tableProps} />
          </div>
          <div
            className="inlineLoading"
            style={!isFetching ? { display: "none" } : {}}
          >
            <InlineLoading description="Loading..." />
          </div>
        </ModuleBody>
      </div>
    </div>
  );
};

export default CompactVendors;
